import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { ComparisonFullDetails } from '@/api/generated/client/models/ComparisonFullDetails';
import { UpdateLineItemRequest, CreateLineItemRequest } from '@/api/generated/client/models';

export function useComparisonMutations(comparisonId: number, canWrite: boolean) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // In read-only mode, block all mutations with a friendly notice instead of
    // firing requests the backend would reject with a 403.
    const guard = <A extends unknown[]>(fn: (...args: A) => unknown) => {
        return (...args: A): void => {
            if (!canWrite) {
                toast({
                    title: 'View-only access',
                    description: 'You do not have permission to edit this comparison.',
                    variant: 'destructive',
                });
                return;
            }
            fn(...args);
        };
    };

    const approveMatchMutation = useMutation({
        mutationFn: async (matchId: number) => {
            return await apiClient.comparisons.updateRoomMatchConfirmationStatus({
                id: matchId,
                updateRoomMatchConfirmationStatusRequest: {
                    confirmationStatus: 'confirmed'
                }
            });
        },
        onSuccess: () => {
            // toast.success("Match approved");
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const rejectMatchMutation = useMutation({
        mutationFn: async (matchId: number) => {
            return await apiClient.comparisons.deleteRoomMatch({ id: matchId });
        },
        onSuccess: () => {
            // toast.success("Match rejected");
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const updateMatchMutation = useMutation({
        mutationFn: async ({ matchId, status, note, insurerRoomIds, contractorRoomIds }: { matchId: number, status?: string, note?: string, insurerRoomIds?: number[], contractorRoomIds?: number[] }) => {
            // First update content
            const response = await apiClient.comparisons.updateRoomMatch({
                id: matchId,
                updateRoomMatchRequest: {
                    note: note,
                    insurerRoomIds,
                    contractorRoomIds
                }
            });

            // If status is provided, update it separately since it's a different endpoint
            if (status) {
                await apiClient.comparisons.updateRoomMatchConfirmationStatus({
                    id: matchId,
                    updateRoomMatchConfirmationStatusRequest: {
                        confirmationStatus: status as any
                    }
                });
            }
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const createMatchMutation = useMutation({
        mutationFn: async ({ insurerRoomIds, contractorRoomIds }: { insurerRoomIds: number[], contractorRoomIds: number[] }) => {
            return await apiClient.comparisons.createRoomMatch({
                id: comparisonId,
                createRoomMatchGroupRequest: {
                    comparisonId,
                    insurerRoomIds,
                    contractorRoomIds
                }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const createLineItemMutation = useMutation({
        mutationFn: async ({ roomId, ...data }: CreateLineItemRequest & { roomId: number }) => {
            return await apiClient.estimates.createLineItem({
                id: roomId,
                createLineItemRequest: data
            });
        },
        onSuccess: () => {
            // Optimistic update removed due to type constraints. Relying on invalidation.
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    // General update line item (for edit modal)
    const updateLineItemMutation = useMutation({
        mutationFn: async ({ itemId, ...data }: UpdateLineItemRequest & { itemId: number }) => {
            return await apiClient.estimates.updateLineItem({
                id: itemId,
                updateLineItemRequest: data
            });
        },
        onSuccess: (updatedLineItem) => {
            queryClient.setQueryData(['comparison', comparisonId], (oldData: ComparisonFullDetails | undefined) => {
                if (!oldData) return oldData;

                const updateItem = (item: any) =>
                    item.lineItemId === updatedLineItem.lineItemId ? updatedLineItem : item;

                return {
                    ...oldData,
                    insurerLineItems: (oldData.insurerLineItems || []).map(updateItem),
                    contractorLineItems: (oldData.contractorLineItems || []).map(updateItem)
                };
            });
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const deleteLineItemMutation = useMutation({
        mutationFn: async (lineItemId: number) => {
            return await apiClient.estimates.deleteLineItem({ id: lineItemId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const bulkApproveMatchesMutation = useMutation({
        mutationFn: async (matchIds: number[]) => {
            // Sequential loop to ensure reliable "batching" behavior
            for (const id of matchIds) {
                try {
                    await apiClient.comparisons.updateRoomMatchConfirmationStatus({
                        id,
                        updateRoomMatchConfirmationStatusRequest: {
                            confirmationStatus: 'confirmed'
                        }
                    });
                } catch (err) {
                    console.error(`Failed to approve match ${id}`, err);
                }
            }
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const createRoomMutation = useMutation({
        mutationFn: async ({ estimateId, name, level }: { estimateId: number, name: string, level?: string }) => {
            return await apiClient.estimates.createRoom({
                estimateId,
                createRoomRequest: { name, level }
            });
        },
        onSuccess: (newRoom, variables) => {
            // Optimistically update the cache to show the new room immediately
            queryClient.setQueryData(['comparison', comparisonId], (oldData: any) => {
                if (!oldData) return oldData;

                const data = oldData as ComparisonFullDetails;

                // Use Number() for robust comparison in case of string IDs from props/URL
                const isInsurer = Number(data.comparison.insurerEstimateId) === Number(variables.estimateId);
                const isContractor = Number(data.comparison.contractorEstimateId) === Number(variables.estimateId);

                if (isInsurer) {
                    return {
                        ...data,
                        insurerRooms: [...(data.insurerRooms || []), newRoom]
                    };
                } else if (isContractor) {
                    return {
                        ...data,
                        contractorRooms: [...(data.contractorRooms || []), newRoom]
                    };
                }

                return data;
            });

            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });



    const approveLineItemMatchGroupMutation = useMutation({
        mutationFn: async (groupId: number) => {
            return await apiClient.comparisons.updateLineItemMatchGroupStatus({
                id: groupId,
                updateMatchGroupStatusRequest: { status: 'user_confirmed' }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const rejectLineItemMatchGroupMutation = useMutation({
        mutationFn: async (groupId: number) => {
            return await apiClient.comparisons.deleteLineItemMatchGroup({ id: groupId });
        },
        onMutate: async (groupId: number) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['comparison', comparisonId] });

            // Snapshot the previous value
            const previousComparison = queryClient.getQueryData(['comparison', comparisonId]);

            // Optimistically update
            queryClient.setQueryData(['comparison', comparisonId], (old: ComparisonFullDetails) => {
                if (!old) return old;
                const data = old;

                // Update insurer items to remove group association
                const newInsurerLineItems = (data.insurerLineItems || []).map(item =>
                    item.lineItemMatchGroupId === groupId
                        ? { ...item, lineItemMatchGroupId: null }
                        : item
                );

                // Update contractor items to remove group association
                const newContractorLineItems = (data.contractorLineItems || []).map(item =>
                    item.lineItemMatchGroupId === groupId
                        ? { ...item, lineItemMatchGroupId: null }
                        : item
                );

                // Remove the group itself
                const newLineItemMatchGroups = (data.lineItemMatchGroups || []).filter(g => g.id !== groupId);

                return {
                    ...data,
                    insurerLineItems: newInsurerLineItems,
                    contractorLineItems: newContractorLineItems,
                    lineItemMatchGroups: newLineItemMatchGroups
                };
            });

            // Return a context object with the snapshotted value
            return { previousComparison };
        },
        onError: (err, newTodo, context) => {
            if (context?.previousComparison) {
                queryClient.setQueryData(['comparison', comparisonId], context.previousComparison);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const bulkApproveLineItemMatchGroupsMutation = useMutation({
        mutationFn: async (groupIds: number[]) => {
            // Sequential loop
            for (const id of groupIds) {
                try {
                    await apiClient.comparisons.updateLineItemMatchGroupStatus({
                        id,
                        updateMatchGroupStatusRequest: { status: 'user_confirmed' }
                    });
                } catch (err) {
                    console.error(`Failed to approve group ${id}`, err);
                }
            }
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const deleteRoomMutation = useMutation({
        mutationFn: async (roomId: number) => {
            return await apiClient.estimates.deleteRoom({ id: roomId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const createLineItemMatchGroupMutation = useMutation({
        mutationFn: async ({ insurerLineItemIds, contractorLineItemIds, roomMatchGroupId }: { insurerLineItemIds: number[], contractorLineItemIds: number[], roomMatchGroupId: number | null }) => {
            console.log('API Request - createLineItemMatchGroup:', { comparisonId, roomMatchGroupId, insurerLineItemIds, contractorLineItemIds });
            return await apiClient.comparisons.createLineItemMatchGroup({
                createLineItemMatchGroupRequest: {
                    comparisonId,
                    roomMatchGroupId,
                    insurerLineItemIds,
                    contractorLineItemIds
                }
            });
        },
        onMutate: async ({ insurerLineItemIds, contractorLineItemIds, roomMatchGroupId }) => {
            await queryClient.cancelQueries({ queryKey: ['comparison', comparisonId] });
            const previousComparison = queryClient.getQueryData(['comparison', comparisonId]);

            queryClient.setQueryData(['comparison', comparisonId], (old: any) => {
                if (!old) return old;
                const data = old as ComparisonFullDetails;

                // 1. Generate temp ID
                const tempGroupId = -Date.now();

                // 2. Identify all moving Item IDs
                const movingInsurerIds = new Set(insurerLineItemIds);
                const movingContractorIds = new Set(contractorLineItemIds);
                const allMovingIds = new Set([...insurerLineItemIds, ...contractorLineItemIds]);

                // 3. Remove items from existing groups to enforce exclusivity (prevent duplication)
                const updatedGroups = data.lineItemMatchGroups.map(group => ({
                    ...group,
                    insurerLineItems: group.insurerLineItems.filter(id => !movingInsurerIds.has(id)),
                    contractorLineItems: group.contractorLineItems.filter(id => !movingContractorIds.has(id))
                })).filter(group =>
                    // Optional: Remove empty groups? Or keep them. Keeping is safer for stability.
                    group.insurerLineItems.length > 0 || group.contractorLineItems.length > 0
                );

                // 4. Create new temp group
                const newGroup: ComparisonFullDetails['lineItemMatchGroups'][number] = {
                    id: tempGroupId,
                    comparisonId,
                    roomMatchGroupId: roomMatchGroupId,
                    status: 'user_confirmed', // Optimistic status
                    confidence: 1,
                    note: null,
                    insurerLineItems: insurerLineItemIds,
                    contractorLineItems: contractorLineItemIds,
                    createdAt: new Date(),
                    updatedAt: new Date()
                };

                // 5. Update Item Details (flat lists) assignment
                const updateItem = (item: any) =>
                    allMovingIds.has(item.lineItemId)
                        ? { ...item, lineItemMatchGroupId: tempGroupId }
                        : item;

                return {
                    ...data,
                    lineItemMatchGroups: [...updatedGroups, newGroup],
                    insurerLineItems: data.insurerLineItems.map(updateItem),
                    contractorLineItems: data.contractorLineItems.map(updateItem)
                };
            });

            return { previousComparison };
        },
        onError: (err: any, newTodo, context) => {
            console.error('Create Group Mutation Failed:', err);
            if (err.response) {
                console.error('Backend Error Response:', err.response.data);
                console.error('Backend Status:', err.response.status);
            }
            if (context?.previousComparison) {
                queryClient.setQueryData(['comparison', comparisonId], context.previousComparison);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const updateLineItemMatchGroupMutation = useMutation({
        mutationFn: async ({ itemId, lineItemMatchGroupId }: { itemId: number, lineItemMatchGroupId: number | null }) => {
            return await apiClient.comparisons.updateLineItemMatchGroup({
                id: itemId,
                updateLineItemMatchGroupRequest: {
                    lineItemMatchGroupId
                }
            });
        },
        onMutate: async ({ itemId, lineItemMatchGroupId }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['comparison', comparisonId] });

            // Snapshot the previous value
            const previousComparison = queryClient.getQueryData(['comparison', comparisonId]);

            // Optimistically update
            queryClient.setQueryData(['comparison', comparisonId], (old: any) => {
                if (!old) return old;
                const data = old as ComparisonFullDetails;

                // Helper to update item in list
                const updateItem = (item: any) =>
                    item.lineItemId === itemId
                        ? { ...item, lineItemMatchGroupId: lineItemMatchGroupId }
                        : item;

                return {
                    ...data,
                    insurerLineItems: data.insurerLineItems.map(updateItem),
                    contractorLineItems: data.contractorLineItems.map(updateItem)
                };
            });

            // Return a context object with the snapshotted value
            return { previousComparison };
        },
        onError: (err, newTodo, context) => {
            if (context?.previousComparison) {
                queryClient.setQueryData(['comparison', comparisonId], context.previousComparison);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure sync
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const moveLineItemsMutation = useMutation({
        mutationFn: async ({ itemIds, targetRoomId, targetLineItemMatchGroupId }: { itemIds: number[], targetRoomId?: number | null, targetLineItemMatchGroupId?: number | null }) => {
            // We need to move items one by one as the API is single-item update
            await Promise.all(itemIds.map(async (id) => {
                // Construct payload dynamically to handle optional fields
                // Casting to any because generated client might not have lineItemMatchGroupId yet
                const payload: UpdateLineItemRequest = {};
                if (targetRoomId !== undefined) payload.roomId = targetRoomId;
                if (targetLineItemMatchGroupId !== undefined) payload.lineItemMatchGroupId = targetLineItemMatchGroupId;

                return await apiClient.estimates.updateLineItem({
                    id,
                    updateLineItemRequest: payload
                });
            }));
        },
        onMutate: async ({ itemIds, targetLineItemMatchGroupId }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: ['comparison', comparisonId] });

            // Snapshot the previous value
            const previousComparison = queryClient.getQueryData(['comparison', comparisonId]);

            // Optimistically update
            queryClient.setQueryData(['comparison', comparisonId], (old: ComparisonFullDetails) => {
                if (!old) return old;
                const data = old;

                // Create sets for fast lookup
                const itemIdSet = new Set(itemIds);

                // Update insurer items
                const newInsurerLineItems = data.insurerLineItems.map(item =>
                    itemIdSet.has(item.lineItemId!)
                        ? { ...item, lineItemMatchGroupId: targetLineItemMatchGroupId ?? null }
                        : item
                );

                // Update contractor items
                const newContractorLineItems = data.contractorLineItems.map(item =>
                    itemIdSet.has(item.lineItemId!)
                        ? { ...item, lineItemMatchGroupId: targetLineItemMatchGroupId ?? null }
                        : item
                );

                return {
                    ...data,
                    insurerLineItems: newInsurerLineItems,
                    contractorLineItems: newContractorLineItems
                };
            });

            // Return a context object with the snapshotted value
            return { previousComparison };
        },
        onError: (err, newTodo, context) => {
            if (context?.previousComparison) {
                queryClient.setQueryData(['comparison', comparisonId], context.previousComparison);
            }
        },
        onSettled: () => {
            // Always refetch after error or success to ensure sync
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const rematchRoomMutation = useMutation({
        mutationFn: async (roomMatchId: number) => {
            return await apiClient.comparisons.rematchLineItems({
                id: comparisonId,
                rematchLineItemsRequest: {
                    roomMatchGroupIds: [roomMatchId]
                }
            });
        },
        onSuccess: () => {
            // Polling or invalidation might be needed if it's async (202), but for now invalidate
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    return {
        approveMatch: guard(approveMatchMutation.mutate),
        isApproving: approveMatchMutation.isPending,
        rejectMatch: guard(rejectMatchMutation.mutate),
        isRejecting: rejectMatchMutation.isPending,
        updateMatch: guard(updateMatchMutation.mutate),
        isUpdating: updateMatchMutation.isPending,
        createMatch: guard(createMatchMutation.mutate),
        isCreating: createMatchMutation.isPending,
        bulkApproveMatches: guard(bulkApproveMatchesMutation.mutate),
        isBulkApproving: bulkApproveMatchesMutation.isPending,
        moveLineItems: guard(moveLineItemsMutation.mutate),
        isMovingLineItems: moveLineItemsMutation.isPending,
        createRoom: guard(createRoomMutation.mutate),
        isCreatingRoom: createRoomMutation.isPending,
        deleteRoom: guard(deleteRoomMutation.mutate),
        rematchRoom: guard(rematchRoomMutation.mutate),
        isRematching: rematchRoomMutation.isPending,
        approveLineItemMatchGroup: guard(approveLineItemMatchGroupMutation.mutate),
        createLineItemMatchGroup: guard(createLineItemMatchGroupMutation.mutate),
        rejectLineItemMatchGroup: guard(rejectLineItemMatchGroupMutation.mutate),
        updateLineItemMatchGroup: guard(updateLineItemMatchGroupMutation.mutate),
        isUpdatingLineItemMatchGroup: updateLineItemMatchGroupMutation.isPending,
        bulkApproveLineItemMatchGroups: guard(bulkApproveLineItemMatchGroupsMutation.mutate),
        isBulkApprovingLineItemMatchGroups: bulkApproveLineItemMatchGroupsMutation.isPending,
        createLineItem: guard(createLineItemMutation.mutate),
        updateLineItem: guard(updateLineItemMutation.mutate),
        deleteLineItem: guard(deleteLineItemMutation.mutate),
        // Legacy/Compat
        unmatchRoom: () => console.warn("Use updateMatch instead")
    };
}
